import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';

import { Input } from '@/components/form/Input';
import { Inputs, Values } from '@/components/form/Inputs';
import { Modal } from '@/components/Modal';
import { useModifyAccount } from '@/hooks';
import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { queryClient } from '@/queryclient';
import { AccountMetadata, LinkAccount } from '@/types';
import { cn } from '@/utils/classname';
import { formatRoleName, ROLE_OPTIONS } from '@/utils/role.util';

interface InviteUserProps {
  open: boolean;
  onClose: () => void;
  customerEmail?: string;
}

/**
 * InviteUser Modal Component
 *
 * Allows adding authorized users to an account. Supports both:
 * - Primary account user invites (when customerEmail is undefined)
 * - Customer account user invites via impersonation (when customerEmail provided)
 *
 * @param open - Controls modal visibility
 * @param onClose - Callback when modal is closed
 * @param customerEmail - Optional email for customer impersonation context
 *
 * @remarks
 * When customerEmail is provided, the useModifyAccount hook will impersonate
 * the customer account for the link operation. This requires appropriate
 * permissions on the authenticated user's account.
 *
 * @example
 * // Primary account invite
 * <InviteUser open={showModal} onClose={handleClose} />
 *
 * // Customer account invite (impersonation)
 * <InviteUser
 *   open={showModal}
 *   onClose={handleClose}
 *   customerEmail="customer@example.com"
 * />
 */
export const InviteUser = (props: InviteUserProps) => {
  const { open, onClose, customerEmail } = props;

  // Pass customerEmail for impersonation context
  const { mutate: link, error } = useModifyAccount(
    'link',
    customerEmail,
    false // Show toast notifications
  );

  const [formValue, setFormValue] = useState<Values>({});
  const enableRbac = useRbacEnabled();

  const handleLink = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const linkAccountData: LinkAccount = {
      username: formValue.username as string,
      config: {} as AccountMetadata,
      role: enableRbac ? (formValue.role as string) : 'admin',
    };

    // Basic validation
    if (!linkAccountData.username || !linkAccountData.username.includes('@')) {
      return;
    }

    // Call mutation with explicit cache invalidation on success
    link(linkAccountData, {
      onSuccess: () => {
        // Explicitly invalidate the account queries to refresh the users list
        if (customerEmail) {
          // Invalidate all queries that include this customer's email
          queryClient.invalidateQueries({
            predicate: query => {
              const queryKey = query.queryKey;
              // Match queries that have 'MY', 'account', and the customerEmail
              return (
                Array.isArray(queryKey) &&
                queryKey[0] === 'MY' &&
                queryKey[1] === 'account' &&
                queryKey.includes(customerEmail)
              );
            },
          });

          // Also invalidate access queries for the customer
          queryClient.invalidateQueries({
            predicate: query => {
              const queryKey = query.queryKey;
              return (
                Array.isArray(queryKey) &&
                queryKey[0] === 'GENERIC_MY_SEARCH' &&
                queryKey.includes('#access') &&
                queryKey.includes(customerEmail)
              );
            },
          });
        } else {
          // For primary account, invalidate standard account queries
          queryClient.invalidateQueries({
            predicate: query => {
              const queryKey = query.queryKey;
              return (
                Array.isArray(queryKey) &&
                queryKey[0] === 'MY' &&
                queryKey[1] === 'account'
              );
            },
          });

          // Also invalidate access queries
          queryClient.invalidateQueries({
            predicate: query => {
              const queryKey = query.queryKey;
              return (
                Array.isArray(queryKey) &&
                queryKey[0] === 'GENERIC_MY_SEARCH' &&
                queryKey.includes('#access')
              );
            },
          });
        }
        onClose();
      },
    });
  };

  return (
    <Modal
      title={'Add User'}
      open={open}
      onClose={onClose}
      className="max-h-[70vh] overflow-y-auto"
      footer={{
        text: 'Add',
        form: 'integration-modal',
      }}
      size={'lg'}
      data-testid="invite-user-modal"
    >
      <div className={cn('align-start flex pt-5 pb-10')}>
        <div className="align-start flex grow flex-col justify-start">
          {error && (
            <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-600">
              <ExclamationTriangleIcon className="mr-2 inline size-5 text-red-700" />
              {error.message || 'Failed to add user. Please try again.'}
            </div>
          )}

          <form
            id={'integration-modal'}
            className="space-y-4"
            onSubmit={handleLink}
          >
            <Inputs
              onChange={setFormValue}
              inputs={[
                {
                  label: 'Email Address',
                  value: '',
                  placeholder: 'email@domain.com',
                  name: 'username',
                  required: true,
                  hidden: false,
                },
                {
                  label: 'Role',
                  value: '',
                  placeholder: 'Select a role',
                  name: 'role',
                  type: Input.Type.SELECT,
                  options: ROLE_OPTIONS.map(opt => ({
                    value: opt.value,
                    label: opt.label,
                  })),
                  required: true,
                  hidden: !enableRbac,
                },
              ]}
            />
            <p className="rounded bg-yellow-100 p-2 text-sm text-yellow-600">
              <ExclamationTriangleIcon className="inline size-5 text-yellow-700" />{' '}
              {enableRbac && formValue.role
                ? `This will grant them ${formatRoleName(formValue.role as string)} access to ${customerEmail ? 'this customer account' : 'your account'}.`
                : `This will grant them full access to ${customerEmail ? 'this customer account' : 'your account'}.`}
            </p>
          </form>
        </div>
      </div>
    </Modal>
  );
};
